import React from 'react';

const Typography = ({ 
  children, 
  variant = 'body',
  className = '', 
  color = 'default',
  align = 'left',
  weight = 'default',
  ...props 
}) => {
  const variantClasses = {
    h1: "text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight",
    h2: "text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight",
    h3: "text-2xl sm:text-3xl lg:text-4xl font-semibold leading-tight",
    h4: "text-xl sm:text-2xl lg:text-3xl font-semibold leading-tight",
    h5: "text-lg sm:text-xl lg:text-2xl font-semibold leading-tight",
    h6: "text-base sm:text-lg lg:text-xl font-semibold leading-tight",
    body: "text-base sm:text-lg leading-relaxed",
    bodySmall: "text-sm sm:text-base leading-relaxed",
    caption: "text-xs sm:text-sm leading-relaxed",
    lead: "text-lg sm:text-xl lg:text-2xl leading-relaxed",
    display: "text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight",
  };

  const colorClasses = {
    default: "text-textPrimary",
    secondary: "text-textSecondary",
    muted: "text-textMuted",
    primary: "text-primary",
    white: "text-white",
    success: "text-success",
    warning: "text-warning",
    error: "text-error",
    gradient: "bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent",
  };

  const alignClasses = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
    justify: "text-justify",
  };

  const weightClasses = {
    light: "font-light",
    normal: "font-normal",
    medium: "font-medium",
    semibold: "font-semibold",
    bold: "font-bold",
    extrabold: "font-extrabold",
    default: "",
  };

  const classes = [
    variantClasses[variant],
    colorClasses[color],
    alignClasses[align],
    weightClasses[weight],
    className
  ].filter(Boolean).join(' ');

  // Determine the appropriate HTML element based on variant
  const getElement = () => {
    if (variant.startsWith('h')) return variant;
    if (variant === 'lead') return 'p';
    if (variant === 'display') return 'h1';
    return 'p';
  };

  const Element = getElement();

  return (
    <Element className={classes} {...props}>
      {children}
    </Element>
  );
};

// Typography sub-components for consistent structure
Typography.H1 = (props) => <Typography variant="h1" {...props} />;
Typography.H2 = (props) => <Typography variant="h2" {...props} />;
Typography.H3 = (props) => <Typography variant="h3" {...props} />;
Typography.H4 = (props) => <Typography variant="h4" {...props} />;
Typography.H5 = (props) => <Typography variant="h5" {...props} />;
Typography.H6 = (props) => <Typography variant="h6" {...props} />;
Typography.Body = (props) => <Typography variant="body" {...props} />;
Typography.BodySmall = (props) => <Typography variant="bodySmall" {...props} />;
Typography.Caption = (props) => <Typography variant="caption" {...props} />;
Typography.Lead = (props) => <Typography variant="lead" {...props} />;
Typography.Display = (props) => <Typography variant="display" {...props} />;

// Section header component for consistent page sections
Typography.SectionHeader = ({ 
  title, 
  subtitle, 
  description,
  align = 'center',
  className = '',
  ...props 
}) => (
  <div className={`mb-8 lg:mb-16 ${className}`} {...props}>
    {subtitle && (
      <Typography 
        variant="bodySmall" 
        color="primary" 
        align={align} 
        weight="semibold"
        className="mb-2 uppercase tracking-wider"
      >
        {subtitle}
      </Typography>
    )}
    
    <Typography 
      variant="h2" 
      color="default" 
      align={align} 
      className="mb-4"
    >
      {title}
    </Typography>
    
    {description && (
      <Typography 
        variant="lead" 
        color="secondary" 
        align={align} 
        className="max-w-3xl mx-auto"
      >
        {description}
      </Typography>
    )}
  </div>
);

export default Typography;
